import React, { useRef } from "react";
import {
  Grid,
  TextField,
  IconButton,
  Typography,
  Switch,
  Box,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { deleteVariantValue, updateVariantStock } from "../api/productApi";
import { useSnackbar } from "notistack";
import TieredPricingManager from "./TieredPricingManager";

type Variant = {
  id?: number;
  variant_name: string;
  price: string;
  stock_quantity: string;
  image?: string;
  sku: string;
  isVisible?: boolean;
  min_order_qty?: string;
  order_multiple?: string;   // ✅ NEW
};


interface VariantManagerProps {
  variants: Variant[];
  productId: number;
  optionNames: string[];
  onUpdateVariant: (
    index: number,
    field: keyof Variant,
    value: string | boolean
  ) => void;
  onRemoveVariant: (index: number) => void;
}

const VariantManager: React.FC<VariantManagerProps> = ({
  variants,
  productId,
  optionNames,
  onUpdateVariant,
  onRemoveVariant,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimeouts = useRef<Record<number, NodeJS.Timeout>>({});

  const handleDeleteVariant = async (originalIndex: number) => {
    const variant = variants[originalIndex];

    try {
      if (variant.id) {
        await deleteVariantValue(variant.id);
      }
      onRemoveVariant(originalIndex);
    } catch (error) {
      enqueueSnackbar("❌ Failed to delete variant", { variant: "error" });
      console.error("❌ Error deleting variant:", error);
    }
  };

  const handleStockChange = (originalIndex: number, value: string) => {
    onUpdateVariant(originalIndex, "stock_quantity", value);
    const stockValue = parseInt(value, 10);
    const variantId = variants[originalIndex].id || null;

    clearTimeout(debounceTimeouts.current[originalIndex]);

    if (isNaN(stockValue) || stockValue < 0) return;

    debounceTimeouts.current[originalIndex] = setTimeout(async () => {
      try {
        await updateVariantStock(productId, variantId, stockValue);
        enqueueSnackbar(" Stock updated successfully", { variant: "success" });
      } catch (error) {
        console.error("❌ Stock update failed:", error);
        // Only show error if the value has changed since the API call was made
        if (variants[originalIndex].stock_quantity === value) {
          enqueueSnackbar("❌ Failed to update stock", { variant: "error" });
        }
      }
    }, 600);
  };

  // Create an array with original indices before grouping
  const variantsWithIndices = variants.map((variant, index) => ({ ...variant, originalIndex: index }));

  const groupedVariants = variantsWithIndices.reduce<Record<string, Array<Variant & { originalIndex: number }>>>(
    (acc, variant) => {
      const key = variant.variant_name.split(":")[0]?.trim() || "Other";
      if (!acc[key]) acc[key] = [];
      acc[key].push(variant);
      return acc;
    }, 
    {}
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        {optionNames.length > 0
          ? `Variants for ${optionNames.join(" and ")}`
          : "Variants"}
      </Typography>

      {Object.entries(groupedVariants).map(([groupName, groupVariants]) => (
        <Accordion key={groupName} defaultExpanded={false} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{groupName}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {groupVariants.map((variant) => (
              <Box
                key={variant.originalIndex}
                sx={{ mb: 5, p: 2, border: "1px solid #ddd", borderRadius: 2 }}
              >
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {variant.variant_name}
                </Typography>

                <Grid container spacing={2} alignItems="center">
                  <Grid size={{xs:12, sm: 3}}>
                    <TextField
                      label="Variant Name"
                      value={variant.variant_name}
                      onChange={(e) =>
                        onUpdateVariant(variant.originalIndex, "variant_name", e.target.value)
                      }
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                  <Grid size={{xs:12, sm: 3}}>
                    <TextField
                      label="SKU"
                      value={variant.sku}
                      onChange={(e) => onUpdateVariant(variant.originalIndex, "sku", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                  <Grid size={{xs:12, sm: 2}}>
                    <TextField
                      label="Price"
                      type="number"
                      value={variant.price}
                      onChange={(e) =>
                        onUpdateVariant(variant.originalIndex, "price", e.target.value)
                      }
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                 <Grid size={{xs:12, sm: 2}}>
  <TextField
    label="Stock Quantity"
    type="number"
    value={variant.stock_quantity}
    onChange={(e) => handleStockChange(variant.originalIndex, e.target.value)}
    fullWidth
    margin="dense"
  />
</Grid>
{/* <Grid container spacing={2}> */}

<Grid size={{xs:12, sm: 2}}>
  <TextField
    label="Min Order Qty"
    type="number"
    value={(variant as any).min_order_qty || "1"}
    onChange={(e) =>
      onUpdateVariant(variant.originalIndex, "min_order_qty" as any, e.target.value)
    }
    fullWidth
    margin="dense"
  />
</Grid>

<Grid size={{xs:12, sm: 2}}>
  <TextField
    label="Order Multiple"
    type="number"
    value={(variant as any).order_multiple || "1"}
    onChange={(e) =>
      onUpdateVariant(variant.originalIndex, "order_multiple" as any, e.target.value)
    }
    fullWidth
    margin="dense"
    helperText="Must order in multiples of this pack size"
  />
</Grid>
{/* </Grid> */}




                  <Grid size={{xs:12, sm: 6}}>
                    <Button variant="outlined" component="label" fullWidth>
                      Upload Variant Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={async (e) => {
                          if (!e.target.files || e.target.files.length === 0) return;
                          const file = e.target.files[0];

                          const formData = new FormData();
                          formData.append("image", file);

                          try {
                            const res = await fetch(
                              "https://gg-api.nationtonation.ca/api/products/upload",
                              {
                                method: "POST",
                                headers: {
                                  Authorization: `Bearer ${
                                    localStorage.getItem("token") || ""
                                  }`,
                                },
                                body: formData,
                              }
                            );
                            const data = await res.json();
                            if (data.imageUrl) {
                              onUpdateVariant(variant.originalIndex, "image", data.imageUrl);
                              enqueueSnackbar(" Image uploaded", {
                                variant: "success",
                              });
                            } else {
                              enqueueSnackbar("Upload failed", { variant: "error" });
                            }
                          } catch (error) {
                            enqueueSnackbar("Upload error", { variant: "error" });
                            console.error("❌ Failed to upload image:", error);
                          }
                        }}
                      />
                    </Button>
                    {variant.image && (
                      <Box mt={1}>
                        <img
                          src={variant.image}
                          alt="Variant Preview"
                          style={{ maxHeight: 100, borderRadius: 4 }}
                        />
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{xs:12, sm: 2}}>
                    <Switch
                      checked={variant.isVisible ?? true}
                      onChange={(e) =>
                        onUpdateVariant(variant.originalIndex, "isVisible", e.target.checked)
                      }
                      color="primary"
                    />
                  </Grid>
                  <Grid size={{xs:12, sm: 2}}>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteVariant(variant.originalIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>

                {variant.id && (
                  <Box sx={{ mt: 3, borderTop: "1px dashed #ccc", pt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Tiered Pricing For "{variant.variant_name}"
                    </Typography>
                    <TieredPricingManager
                      productId={productId}
                      variantId={variant.id}
                    />
                  </Box>
                )}

                <Divider sx={{ mt: 3 }} />
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default VariantManager;